/**
 * Exakte Geld-Arithmetik in Mikro-Einheiten (CLAUDE.md §6: keine Float-Arithmetik
 * mit Geld). Beträge bleiben durchgehend `bigint`; nur die *nicht-monetären*
 * Eingaben Menge/Rabatt/Satz werden auf Ganzzahlen skaliert (Menge → Mikro-Stück,
 * Prozente → Basispunkte). Damit entfällt jeglicher Float-Rundungsfehler auf
 * Geldwerten — auch jenseits von 2^53 Mikro-Einheiten.
 *
 * Single Source of Truth: von Backend (Beleg-Mapper) UND Frontend (Live-Vorschau)
 * genutzt, damit Vorschau und persistierter Beleg garantiert identisch rechnen.
 */

const MICRO = 1_000_000n; // Mikro-Einheiten je Stück (für die Mengenskalierung)
const BP = 10_000n; // Basispunkte je 100 % (Prozent × 100)

/** Kaufmännische Rundung einer bigint-Division (round half away from zero). */
export function roundedDiv(numerator: bigint, divisor: bigint): bigint {
  if (divisor === 0n) return 0n;
  const negative = numerator < 0n !== divisor < 0n;
  const a = numerator < 0n ? -numerator : numerator;
  const d = divisor < 0n ? -divisor : divisor;
  const q = (a + d / 2n) / d;
  return negative ? -q : q;
}

/** Betrag × (fraktionaler) Faktor, z. B. Stundensatz × Stunden. */
export function scaleAmount(amount: bigint, factor: number): bigint {
  const factorScaled = BigInt(Math.round(factor * Number(MICRO)));
  return roundedDiv(amount * factorScaled, MICRO);
}

/** Zähler des Zeilen-Nettos; Nenner ist `MICRO * BP` (einmal gerundet, s. unten). */
function lineNetNumerator(unitPriceAmount: bigint, quantity: number, discountPercent?: number | null): bigint {
  const qtyScaled = BigInt(Math.round(quantity * Number(MICRO)));
  const keptBp = BigInt(Math.round((100 - (discountPercent ?? 0)) * 100)); // Basispunkte von (1−Rabatt)
  return unitPriceAmount * qtyScaled * keptBp;
}

/** Zeilen-Netto = Einzelpreis × Menge × (1 − Rabatt%), exakt in Mikro-Einheiten. */
export function lineNet(
  unitPriceAmount: bigint,
  quantity: number,
  discountPercent?: number | null,
): bigint {
  return roundedDiv(lineNetNumerator(unitPriceAmount, quantity, discountPercent), MICRO * BP);
}

/** Steuerbetrag = Netto × Satz%, exakt in Mikro-Einheiten. */
export function applyTaxRate(net: bigint, taxRatePercent: number): bigint {
  return roundedDiv(net * taxRateBp(taxRatePercent), BP);
}

function taxRateBp(taxRatePercent: number): bigint {
  return BigInt(Math.round(taxRatePercent * 100));
}

/** Mikro-Einheiten je Cent (1/100 der Haupteinheit — EUR, USD, CHF). */
export const MICROS_PER_CENT = 10_000n;

/** Betrag kaufmännisch auf ganze Cent runden (half away from zero), in Mikro-Einheiten. */
export function roundToCents(amount: bigint): bigint {
  return roundedDiv(amount, MICROS_PER_CENT) * MICROS_PER_CENT;
}

/**
 * Bruch `numerator / divisor` (Ergebnis in Mikro-Einheiten) in EINEM Schritt auf
 * Cent runden — erst auf Mikro und dann auf Cent zu runden, kippt Grenzfälle
 * (0,0049995 € → 0,005000 € → 0,01 € statt 0,00 €).
 */
export function roundFractionToCents(numerator: bigint, divisor: bigint): bigint {
  return roundedDiv(numerator, divisor * MICROS_PER_CENT) * MICROS_PER_CENT;
}

/*
 * Rundungsmodell der Belege (Review 2026-09-28, Befund 1): gebucht wird, was
 * gedruckt wird. Das PDF zeigt Zeilen, Netto, USt und Brutto auf Cent — also
 * werden genau diese Werte gespeichert, nicht die Mikro-Zwischenstände
 * (vorher: 100,01 € netto → gebucht 120,012 € brutto, gedruckt 120,01 €; wer
 * den gedruckten Betrag zahlte, blieb „offen").
 *
 *   Zeile  = Preis × Menge × (1 − Rabatt), auf Cent
 *   Netto  = Σ gerundete Zeilen (= Summe dessen, was in der Spalte steht)
 *   USt    = Netto × Satz, auf Cent (einmal je Beleg, nicht je Zeile)
 *   Brutto = Netto + USt
 *
 * Einzelpreise bleiben unverändert genau (bis Mikro) — gerundet wird erst das
 * Zeilenergebnis.
 */

/** Zeilen-Netto eines Belegs: exakt gerechnet, dann auf Cent gerundet (was gedruckt wird). */
export function documentLineNet(
  unitPriceAmount: bigint,
  quantity: number,
  discountPercent?: number | null,
): bigint {
  return roundFractionToCents(lineNetNumerator(unitPriceAmount, quantity, discountPercent), MICRO * BP);
}

/**
 * Belegsummen aus (bereits gerundeten) Zeilen-Nettos. Rundet jede Zeile
 * vorsorglich noch einmal — eine ungerundete Eingabe darf das Modell nicht
 * unterlaufen.
 */
export function documentTotals(
  lineNets: readonly bigint[],
  taxRatePercent: number,
): { net: bigint; tax: bigint; gross: bigint } {
  let net = 0n;
  for (const line of lineNets) net += roundToCents(line);
  const tax = roundFractionToCents(net * taxRateBp(taxRatePercent), BP);
  return { net, tax, gross: net + tax };
}

/**
 * Offener Betrag eines Postens (Brutto − Zahlungen), auf Cent. Ein Rest unter
 * einem halben Cent gilt als beglichen: Altbelege aus der Zeit vor dem
 * Rundungsmodell tragen Sub-Cent-Bruttos (120,012 €), gedruckt und bezahlt
 * wurden 120,01 € — ohne diese Rundung blieben sie für immer „offen 0,00 €".
 */
export function openAmount(gross: bigint, paid: bigint): bigint {
  return roundToCents(gross - paid);
}
